import { defineAbility } from '@casl/ability';
import { RuleAlias } from "../entities";
import { AUTH_RESOURCES } from './Resources';
const RESOURCE_RESOLVER = {
    [AUTH_RESOURCES.WIZARD]: [
        'protocolType',
        'protocolContentType',
        'protocolTypeGroup',
        'book',
        'equipmentList',
        'plausibilityCheck',
        'etmal-calculation',
        'book-arrangement',
        'book-configuration',
        'file',
        'view',
        'wizard',
    ],
    [AUTH_RESOURCES.PROTOCOL]: [
        'protocol',
        'protocolDraft',
        'record-file',
        'noon-report-dispatch',
        'navigation-data',
        { action: 'read', otherResource: 'book-key-bundle', otherAction: 'read' },
        { action: 'create', otherResource: 'book-key-bundle', otherAction: 'create' },
    ],
    [AUTH_RESOURCES.AUDIT_LOG]: ['auditLog'],
    [AUTH_RESOURCES.VESSEL_PARTICULARS]: [
        'record-file',
        'vessel',
        { action: 'update', otherResource: 'vessel', otherAction: 'create' },
    ],
    [AUTH_RESOURCES.VOYAGE]: ['voyage'],
    [AUTH_RESOURCES.CREW_LIST]: ['crewList'],
    [AUTH_RESOURCES.EQUIPMENT]: ['equipmentItem', 'equipment-item-exchange'],
    [AUTH_RESOURCES.APPROVAL]: ['approval'],
    [AUTH_RESOURCES.FIRST_APPROVAL]: ['approval'],
    [AUTH_RESOURCES.SECOND_APPROVAL]: ['approval'],
    [AUTH_RESOURCES.USER]: [
        'user',
        'role',
        { action: 'create', otherResource: 'user', otherAction: 'update' },
        'book-key-bundle',
    ],
    [AUTH_RESOURCES.CLIENT_DEVICE]: ['clientDevice'],
    [AUTH_RESOURCES.INSTALLATION_WIZARD]: [
        'installation-state',
        { action: 'read', otherResource: 'installation-state', otherAction: 'create' },
        { action: 'read', otherResource: 'installation-state', otherAction: 'patch' },
    ],
    [AUTH_RESOURCES.ORDER]: ['order'],
    [AUTH_RESOURCES.RESPONSIBILITY]: ['responsibility'],
    [AUTH_RESOURCES.SOUNDING_SPOT]: ['sounding-spot'],
    [AUTH_RESOURCES.BOOK_CONFIGURATION]: ['book-configuration'],
    [AUTH_RESOURCES.CHECKLIST_TYPE]: ['checklistType', 'checklist-type-exchange'],
    [AUTH_RESOURCES.SYSTEM_UPDATE]: [
        'system-update',
        { action: 'create', otherResource: 'system-update-notifier', otherAction: 'manage' },
    ],
    [AUTH_RESOURCES.LICENSE]: ['license-import'],
    [AUTH_RESOURCES.BACKUP]: [
        'backup',
        { action: 'read', otherResource: 'backup', otherAction: 'create' },
        { action: 'create', otherResource: 'restore-backup', otherAction: 'create' },
        { action: 'create', otherResource: 'restore-backup-notifier', otherAction: 'manage' },
    ],
    [AUTH_RESOURCES.SHIP_NETWORK_SETTINGS]: ['systemSettings'],
    [AUTH_RESOURCES.LWE_NETWORK_SETTINGS]: ['systemSettings'],
    [AUTH_RESOURCES.AIS_SETTINGS]: ['systemSettings'],
    [AUTH_RESOURCES.AUTOMATED_RECORDING_SETTINGS]: ['systemSettings'],
    [AUTH_RESOURCES.SYSTEM_TIME_SETTINGS]: [
        'manual-time-setting',
        { action: 'update', otherResource: 'manual-time-setting', otherAction: 'create' },
    ],
    [AUTH_RESOURCES.RESET_CLOUD_CONNECTION]: ['reset-cloud-connection'],
    [AUTH_RESOURCES.LOGS]: ['logs', { action: 'read', otherResource: 'logs', otherAction: 'create' }],
    [AUTH_RESOURCES.TLS_CERTIFICATE]: ['tls-certificate'],
    [AUTH_RESOURCES.RESTART]: ['restart'],
};
const PTC_MASTER_ONLY_RESOURCES = [AUTH_RESOURCES.WIZARD];
export default class ActionManager {
    static reference;
    actionMap;
    constructor() {
        this.actionMap = new Map();
    }
    static getReference() {
        if (!ActionManager.reference) {
            ActionManager.reference = new ActionManager();
        }
        return ActionManager.reference;
    }
    addAction(action) {
        if (this.actionMap.has(action.alias)) {
            throw new Error(`Action with alias ${action.alias} already exists.`);
        }
        this.actionMap.set(action.alias, action);
    }
    getAction(alias) {
        if (!this.actionMap.has(alias)) {
            throw new Error(`Action with alias ${alias} does not exist.`);
        }
        return this.actionMap.get(alias);
    }
    getAbility(user, { ptcMaster } = { ptcMaster: false }) {
        const ability = defineAbility((can, cannot) => {
            can(['create', 'patch', 'remove'], 'clientDevice');
            can('read', 'systemStatus');
            can(['create', 'update'], 'sync');
            can(['create', 'read'], 'logs');
            can('read', 'auditLog');
            can('read', 'user');
            can('read', 'role');
            can('read', 'alert');
            can('read', 'book-configuration');
            can('read', 'protocolType');
            can('read', 'protocolContentType');
            can('read', 'protocolTypeGroup');
            can('read', 'book');
            can('read', 'file');
            can('read', 'equipmentList');
            can('read', 'plausibilityCheck');
            can('read', 'view');
            can('read', 'book-arrangement');
            can('read', 'etmal-calculation');
            can('read', 'protocol');
            can('read', 'protocolDraft');
            can('read', 'approval');
            can('read', 'noon-report-dispatch');
            can('read', 'equipmentItem');
            can('read', 'error-log');
            can('read', 'vessel');
            can('read', 'crewList');
            can('read', 'voyage');
            can('read', 'record-file');
            can('read', 'order');
            can('read', 'sounding-spot');
            can('read', 'responsibility');
            can('read', 'checklistType');
            can('read', 'book-key-bundle');
            can('read', 'systemInfo');
            if (user) {
                can('create', 'change-password');
                can('create', 'pdf-sign');
                can('read', 'ship');
                const aliases = user.roles.reduce((currentAliases, role) => {
                    return [...currentAliases, ...role.rules.map((rule) => rule.alias)];
                }, []);
                this.actionMap.forEach((action) => {
                    if ((!ptcMaster && PTC_MASTER_ONLY_RESOURCES.includes(action.resourceToModify)) ||
                        action.alias === RuleAlias.READ_ALL) {
                        return;
                    }
                    const resourceAliases = RESOURCE_RESOLVER[action.resourceToModify] || [];
                    if (action.isAllowed(aliases) || (aliases.includes(RuleAlias.READ_ALL) && action.actionName === 'read')) {
                        can(action.actionName, action.resourceToModify);
                        resourceAliases.forEach((resource) => {
                            if (typeof resource === 'string') {
                                can(action.actionName, resource);
                            }
                            else if (action.actionName === resource.action) {
                                can(resource.otherAction, resource.otherResource);
                            }
                        });
                    }
                    else if (![RuleAlias.MANAGE_ALL, RuleAlias.READ_ALL].includes(action.alias)) {
                        cannot(action.actionName, action.resourceToModify);
                    }
                });
            }
        });
        return ability;
    }
}
//# sourceMappingURL=ActionManager.js.map